/**
 * Referral attribution helpers for the intake form.
 *
 * Captures ?r=<code> from the URL into a .onecalla.com cookie (shared across
 * sub-apps) and per-origin localStorage, both with a 30-day TTL. Read priority
 * at submit time: cookie first (cross-subdomain), then localStorage.
 *
 * Query param is `r` to avoid colliding with `?ref=audit`, which is already
 * used as a source marker by the quick-audit funnel.
 */

const STORAGE_KEY = "onecalla_ref";
const COOKIE_KEY = "onecalla_ref";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const TTL_SECONDS = 30 * 24 * 60 * 60;
const PARAM = "r";
const COOKIE_DOMAIN = ".onecalla.com";
const CODE_RE = /^[A-Za-z0-9_-]{4,40}$/;

interface StoredRef {
  code: string;
  ts: number;
}

function isValidCode(code: unknown): code is string {
  return typeof code === "string" && CODE_RE.test(code);
}

function readLocal(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return isValidCode(parsed.code) ? parsed.code : null;
  } catch {
    return null;
  }
}
function writeLocal(code: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch {
    /* private mode or quota -- best effort */
  }
}
function readCookie(): string | null {
  try {
    const match = document.cookie.split("; ").find((row) => row.indexOf(`${COOKIE_KEY}=`) === 0);
    if (!match) return null;
    const value = decodeURIComponent(match.slice(COOKIE_KEY.length + 1));
    return isValidCode(value) ? value : null;
  } catch {
    return null;
  }
}
function writeCookie(code: string): void {
  try {
    document.cookie =
      `${COOKIE_KEY}=${encodeURIComponent(code)}` +
      `; domain=${COOKIE_DOMAIN}` +
      `; path=/` +
      `; max-age=${TTL_SECONDS}` +
      `; samesite=lax` +
      `; secure`;
  } catch {
    /* ignore */
  }
}

/**
 * Read ?r= from the URL. If valid, persist it (cookie + localStorage) and
 * return it. Otherwise fall back to cookie, then localStorage. Call once on
 * mount.
 */
export function captureReferrerCode(): string | null {
  try {
    const urlCode = new URLSearchParams(window.location.search).get(PARAM);
    if (isValidCode(urlCode)) {
      writeLocal(urlCode);
      writeCookie(urlCode);
      return urlCode;
    }
  } catch {
    /* URL API unavailable in some embeds */
  }
  return readCookie() || readLocal();
}

/** Get the stored code without re-reading the URL. Call at submit time. */
export function getReferrerCode(): string | null {
  return readCookie() || readLocal();
}
